import { Grid, makeStyles, Typography, Tooltip, useMediaQuery } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import React, { memo } from 'react';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const shouldDisplayRegion = (region) => {
    let result = false;
    if (!region) return result;
    if (Array.isArray(region.Subregions)) {
        result = result || region.Subregions.some((sr) => shouldDisplayRegion(sr));
    }
    if (Array.isArray(region.Results)) {
        result = result || region.Results.some((v) => !!v && v.ShouldShow);
    }
    return result;
};

export const QueryResultPanels = memo(({ queryResult, isBigTitleUsed }) => {
    const isAllSingleRow = useMediaQuery('(min-width:500px)');
    return (
        <Grid item container justifyContent="center" direction="column" style={{ marginTop: '16px' }}>
            {queryResult.Regions.filter(shouldDisplayRegion).map((r, i) => {
                return (
                    <Grid item style={{ marginTop: '16px' }}>
                        <QueryResultPanel region={r} isBigTitleUsed={isBigTitleUsed} key={i} isAllSingleRow={isAllSingleRow} />
                    </Grid>
                );
            })}
        </Grid>
    );
});
QueryResultPanels.displayName = 'QueryResultPanels';

const QueryResultSection = memo(({ result, isBigTitleUsed, isAllSingleRow }) => {
    const { Title, UiValue, ShouldShow, UiValueLong } = result;
    return (
        <React.Fragment key={Title}>
            {ShouldShow && (
                <Grid container item direction="column">
                    <Grid container item direction={isAllSingleRow ? 'row' : 'column'} style={{ marginTop: '2px', marginBottom: '2px' }}>
                        <Grid item style={{ width: isAllSingleRow ? '275px' : '100%' }}>
                            <Typography variant={'overline'}>{Title}</Typography>
                        </Grid>
                        <Grid item style={{ marginLeft: '25px', marginTop: !isAllSingleRow && '-10px' }}>
                            <Grid container direction="row">
                                <Grid item>
                                    <Typography variant={isBigTitleUsed ? 'body2' : 'caption'}>{UiValue}</Typography>
                                </Grid>
                                <Grid item>
                                    {!!UiValueLong && UiValueLong !== UiValue && (
                                        <Tooltip title={UiValueLong} style={{ marginTop: '-2px', marginLeft: '2px' }}>
                                            <InfoIcon />
                                        </Tooltip>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </React.Fragment>
    );
});
QueryResultSection.displayName = 'QueryResultSection ';

// const QueryResultSubregion = ({ region, isBigTitleUsed }) => {
//     const { Results, Name, NameFormat, Subregions } = region;
//     return (
//         <Grid item container direction="column">
//             <Grid item>
//                 <Typography variant="h6">
//                     {Name}
//                 </Typography>
//             </Grid>
//             <Grid item container>
//                 {
//                     Results.map(r => <QueryResultSection result={r} isBigTitleUsed={isBigTitleUsed} />)
//                 }
//                 {
//                     Array.isArray(Subregions) && Subregions.filter(sr => !!sr).filter(shouldDisplayRegion).map((sr, k) => <QueryResultSubregion key={k} isBigTitleUsed={isBigTitleUsed} region={sr} />)
//                 }
//             </Grid>
//         </Grid>
//     );
// }

const QueryResultPanel = memo(({ region, isBigTitleUsed, isAllSingleRow, isExpanded = true, titleVariant = undefined }) => {
    const { Results, Name, Subregions, IsWarning } = region;
    return (
        <>
            <style
                dangerouslySetInnerHTML={{
                    __html: `.MuiExpansionPanel-root:before { background: unset !important }`
                }}
            />
            <ExpansionPanel elevation={2} defaultExpanded={isExpanded}>
                <ExpansionPanelSummary
                    expandIcon={<ExpandMoreIcon fontSize="large" color="black" />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    style={{ padding: isAllSingleRow ? undefined : '8px' }}
                >
                    <Typography
                        variant={titleVariant || (isBigTitleUsed ? 'h4' : 'h5')}
                        style={{
                            fontWeight: '500',
                            wordWrap: 'break-word',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {Name} {IsWarning && <WarningIcon style={{ marginLeft: '8px', fill: '#c2185b' }} />}
                    </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails style={{ padding: isAllSingleRow ? undefined : '8px' }}>
                    <Grid container direction="column">
                        {Results.map((r) => (
                            <QueryResultSection result={r} isBigTitleUsed={isBigTitleUsed} isAllSingleRow={isAllSingleRow} />
                        ))}
                        {Array.isArray(Subregions) &&
                            Subregions.filter((sr) => !!sr)
                                .filter(shouldDisplayRegion)
                                .map((sr, k) => (
                                    <QueryResultPanel
                                        key={k}
                                        isBigTitleUsed={isBigTitleUsed}
                                        region={sr}
                                        titleVariant={isBigTitleUsed ? 'h6' : 'body1'}
                                        isAllSingleRow={isAllSingleRow}
                                    />
                                ))}
                    </Grid>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        </>
    );
});
QueryResultPanel.displayName = 'QueryResultPanel';
// eslint-disable-next-line
const useStyles = makeStyles((theme) => ({
    heading: {
        marginLeft: theme.spacing(2),
        paddingTop: theme.spacing(2),
        '&.inline': {
            marginTop: 'auto',
            marginRight: theme.spacing(2),
            width: 70,
            textAlign: 'center',
            paddingBottom: theme.spacing(1)
        }
    },
    result: {
        padding: theme.spacing(1, 2),
        '&.title': {},
        '&.value': {
            fontWeight: 'bold'
        }
    },
    previewItem: {
        padding: theme.spacing(1, 2),
        marginRight: theme.spacing(2),
        fontSize: 16
    },
    resultItemsContainer: {
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        //height: 450,
        placeContent: 'flex-start',
        padding: theme.spacing(2, 1, 1, 1),
        overflow: 'auto'
    }
}));
